/**
 * Base → market price lookup
 * -----------------------------------------------------------------------------
 * SearchSpring indexes prices in the shop's base currency and has no
 * market-aware price fields, so its price facets and range filters are always
 * base-currency values. Range filters must keep being *sent* in base currency —
 * that's what the index holds — but every amount shown to a shopper has to be a
 * real market price, or it won't line up with the product cards.
 *
 * The localisation pass already resolves both prices for each product, so it
 * records those pairs here and the price filter reads its labels straight out of
 * them. No conversion rate is involved: a displayed price is always a price some
 * product on the page actually has. Pairs accumulate as further pages load.
 */

import { useEffect, useState } from 'preact/hooks'

interface PricePair {
  base: number
  market: number
}

let pricePairs: PricePair[] = []
const listeners = new Set<() => void>()

function notifyMarketPrices() {
  listeners.forEach((listener) => listener())
}

function subscribeMarketPrices(listener: () => void) {
  listeners.add(listener)
  return () => listeners.delete(listener)
}

/** Re-render when newly localised prices land so filter labels stay in sync. */
function useMarketPrices() {
  const [version, setVersion] = useState(0)

  useEffect(() => {
    return subscribeMarketPrices(() => setVersion((current) => current + 1))
  }, [])

  return version
}

/** Record real base/market price pairs resolved while localising results. */
function recordMarketPrices(pairs: Array<[number, number]>): void {
  const merged = new Map(pricePairs.map((pair) => [pair.base, pair]))

  for (const [base, market] of pairs) {
    if (!(base > 0) || !(market > 0)) continue
    merged.set(base, { base, market })
  }

  pricePairs = [...merged.values()].sort((first, second) => first.base - second.base)
  notifyMarketPrices()
}

function nearestPair(base: number): PricePair | null {
  if (!pricePairs.length) return null

  return pricePairs.reduce((closest, pair) => {
    return Math.abs(pair.base - base) < Math.abs(closest.base - base) ? pair : closest
  })
}

/**
 * The market price recorded against a base amount. Exact matches win; otherwise
 * interpolate between surrounding recorded prices so a range's min cannot land
 * above its max. Null on the base market, where no localisation runs.
 */
function getMarketPrice(base: number): number | null {
  if (!pricePairs.length) return null

  const exact = pricePairs.find((pair) => pair.base === base)
  if (exact) return exact.market

  if (base <= pricePairs[0].base) return pricePairs[0].market

  const last = pricePairs[pricePairs.length - 1]
  if (base >= last.base) return last.market

  for (let index = 1; index < pricePairs.length; index++) {
    const prev = pricePairs[index - 1]
    const next = pricePairs[index]
    if (base > next.base) continue

    const span = next.base - prev.base
    if (span === 0) return prev.market

    return prev.market + ((base - prev.base) / span) * (next.market - prev.market)
  }

  return last.market
}

/**
 * Snap a base amount onto a price a product actually has, so a slider thumb, its
 * label and the range sent to the index can never describe different amounts.
 */
function snapToRecordedPrice(base: number): number {
  return nearestPair(base)?.base ?? base
}

export { recordMarketPrices, getMarketPrice, snapToRecordedPrice, useMarketPrices }
